import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-typical-users';

/**
 * Typical-users profile card describing who uses a SignNow workflow.
 * Single blue background card with heading, subtitle, a single-column checkmark list of user types, and a footer note.
 * Lists user audiences as plain strings with check icons, unlike who-users type-1 which renders a two-column quote layout.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for typical users of {keyword}. 6-10 words.
 * @slot subtitle - <me-text variant="large">
 * @example
 *   <sn-typical-users
 *     items='[{"text":"Musicians collaborating on original tracks."},{"text":"Actors involved in theater or film productions."},{"text":"Content creators working on joint YouTube or podcast projects."},{"text":"Authors co-writing books or scripts."}]'
 *     footer="This agreement is commonly used across creative and professional industries."
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Who typically uses this?</me-heading>
 *     <me-text slot="subtitle" variant="large">This agreement is used by various professionals who need to formalize creative collaborations.</me-text>
 *   </sn-typical-users>
 */
@Component({
  tag: 'sn-typical-users',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcUserProfilesList {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Checkmark list items describing typical users.
   * Each item can be a plain string or an object with a `text` property.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} text - Profile of this user type: their role, industry, and specific use case for {keyword}. You MUST write 30-50 words.
   * @item.required text
   * @item.content text
   * @minItems 2
   * @maxItems 2
   */
  @Prop({ mutable: true }) items?: (string | { text: string })[] | string;

  /**
   * Footer text shown below the checklist.
   * @content
   * @type {string}
   */
  @Prop() footer?: string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` / `footer` props.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} items - Fallback list items (use items prop instead)
   * @property {string} footer - Fallback footer text (use footer prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        items?: string[];
        footer?: string;
        raw_html?: string;
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);

    const footer = this.footer ?? values?.footer;

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__wrapper`}>
          <me-row direction="column" directionLg="column">
            <me-column spanLg="12">
              <slot name="heading">
                {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
              </slot>
              <slot name="subtitle">
                {values?.subtitle && <me-text variant="large">{values.subtitle}</me-text>}
              </slot>
              {items.length > 0 && (
                <ul class={`${COMPONENT_NAME}__list`}>
                  {items.map((item) => {
                    const text = typeof item === 'string' ? item : item?.text;
                    return text ? (
                      <li class={`${COMPONENT_NAME}__item`}>
                        <me-icon name="interface--check" class={`${COMPONENT_NAME}__check`} width="20" height="20" lazy={false}></me-icon>
                        <me-text tag="span">{text}</me-text>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
              {footer && <me-text class={`${COMPONENT_NAME}__footer`}>{footer}</me-text>}
            </me-column>
          </me-row>
        </div>
      </me-section>
    );
  }
}
